'use client';

import { useRef, useEffect, useState } from 'react';
import { Button } from '#/components/ui/button';
import {
  DownloadPng,
  DownloadSvg,
  RenderMorphemViewer
} from '@bareun/morphemeviewer';
import AnalyzeTable from '../analyzeTable';
import JsonPar from '../../components/json-par';
import BgBaseCard from '#/components/bg-base-card';
import { toast } from 'react-toastify';
import { ArrowLeft } from 'lucide-react';
import { trackEvent } from '#/lib/analytics';

interface AnalyzeResultsSectionProps {
  sentences: any[];
  json: any;
  onBack: () => void;
  /** 의미 구분(WSD)을 켜고 요청했는지. 표에서 안내·일괄 펼침 노출을 결정한다. */
  withSense?: boolean;
}

export default function AnalyzeResultsSection({
  sentences,
  json,
  onBack,
  withSense = false
}: AnalyzeResultsSectionProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [tag, setTag] = useState<'tag' | 'table' | 'json'>('tag');
  const [width, setWidth] = useState<number>(1280);

  /**
   * 보기 전환을 GA4 로 남긴다. 기본값이 '형태소 태깅' 이라 여기 들어오는 신호는
   * 곧 "기본 화면 말고 다른 표현을 찾아봤다"는 뜻이다 — 테이블·JSON 이 실제로
   * 쓰이는지, 아니면 태깅 그림만 보고 끝내는지가 갈린다.
   */
  const changeView = (next: 'tag' | 'table' | 'json') => {
    if (next === tag) return;
    trackEvent('view_result', {
      tool: 'analyze',
      view: next,
      from_view: tag,
      sentence_count: sentences.length
    });
    setTag(next);
  };

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setWidth(entry.contentRect.width - 100);
      }
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (sentences.length > 0 && tag === 'tag') {
      RenderMorphemViewer('div.app', sentences, width);
    }
  }, [sentences, width, tag]);

  return (
    <BgBaseCard>
      <div className="flex flex-col h-full gap-2" ref={containerRef}>
        {/* 헤더: 라벨 + 새 분석 */}
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold text-foreground/70">
            형태소 분석
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              trackEvent('new_analysis', { tool: 'analyze' });
              onBack();
            }}
            className="gap-1.5 h-7 text-xs border-primary/40 text-primary hover:bg-primary hover:text-white"
          >
            <ArrowLeft className="size-3.5" />새 분석
          </Button>
        </div>

        {/* 탭 + 액션 버튼 */}
        <div className="flex flex-wrap gap-2 items-center justify-between">
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="sm"
              className={tag === 'tag' ? 'text-[#104573]' : ''}
              onClick={() => changeView('tag')}
            >
              형태소 태깅
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className={tag === 'table' ? 'text-[#104573]' : ''}
              onClick={() => changeView('table')}
            >
              형태소 테이블
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className={tag === 'json' ? 'text-[#104573]' : ''}
              onClick={() => changeView('json')}
            >
              JSON
            </Button>
          </div>

          <div className="flex gap-2">
            {tag === 'tag' && sentences.length > 0 && (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    trackEvent('download_result', {
                      tool: 'analyze',
                      format: 'svg',
                      sentence_count: sentences.length
                    });
                    DownloadSvg('svg.viewer');
                  }}
                >
                  SVG 다운로드
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    trackEvent('download_result', {
                      tool: 'analyze',
                      format: 'png',
                      sentence_count: sentences.length
                    });
                    DownloadPng('svg.viewer');
                  }}
                >
                  PNG 다운로드
                </Button>
              </>
            )}
            {tag === 'json' && sentences.length > 0 && (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    trackEvent('click_copy', {
                      tool: 'analyze',
                      format: 'json',
                      sentence_count: sentences.length
                    });
                    navigator.clipboard
                      .writeText(JSON.stringify(json, null, 2))
                      .then(() => toast.success('JSON 복사 완료되었습니다'))
                      .catch((err) => {
                        toast.error('복사 실패');
                        console.error('복사 실패:', err);
                      });
                  }}
                >
                  JSON 복사
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    trackEvent('download_result', {
                      tool: 'analyze',
                      format: 'json',
                      sentence_count: sentences.length
                    });
                    const blob = new Blob([JSON.stringify(json, null, 2)], {
                      type: 'application/json'
                    });
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = 'analysis_result.json';
                    link.click();
                    URL.revokeObjectURL(url);
                  }}
                >
                  JSON 다운로드
                </Button>
              </>
            )}
          </div>
        </div>

        {/* 콘텐츠 */}
        <div className="flex-1 min-h-[120px] overflow-auto">
          {sentences.length > 0 && tag === 'tag' && (
            <div id="app" className="app" />
          )}
          {sentences.length > 0 && tag === 'json' && <JsonPar data={json} />}
          {sentences.length > 0 && tag === 'table' && (
            <AnalyzeTable sentences={sentences} withSense={withSense} />
          )}
          {sentences.length === 0 && (
            <span className="text-sm text-muted-foreground">
              분석 결과가 없습니다.
            </span>
          )}
        </div>
      </div>
    </BgBaseCard>
  );
}
